import { useCallback, useEffect, useState } from 'react';
import { GitCompare, History, Loader2, RotateCcw, Save, X } from 'lucide-react';
import {
  ROTULO_MOTIVO,
  compararVersoes,
  formatarMomento,
  listarVersoes,
  rotuloStatus,
  type DiferencaVersao,
  type ManualVersaoResumo,
} from '../manual-api';
import type { ManualEditor } from '../use-manual-editor';

/** Nomes técnicos do modelo traduzidos para a linguagem do documento. */
const NOME_CAMPO: Record<string, string> = {
  nomeMaquina: 'Nome da máquina',
  identificacao: 'Identificação / TAG',
  modeloTipo: 'Modelo ou tipo',
  numeroSerie: 'Número de série',
  anoFabricacao: 'Ano de fabricação',
  codigoRastreabilidade: 'Código de rastreabilidade',
  versao: 'Revisão',
  dataEmissao: 'Data de emissão',
  dataLevantamento: 'Data do levantamento',
  localLevantamento: 'Local do levantamento',
  responsavelLevantamento: 'Responsável pelo levantamento',
  fabricante: 'Fabricante',
  contratante: 'Contratante',
  setor: 'Setor',
  imagemCapa: 'Imagem de capa',
  documentosConsultados: 'Materiais consultados',
  publicoAlvo: 'Público-alvo',
  normas: 'Normas aplicáveis',
  usoDevido: 'Uso devido',
  usosProibidos: 'Usos proibidos',
  precaucoes: 'Precauções gerais',
  epcs: 'EPC',
  epis: 'EPI',
  treinamentos: 'Treinamentos',
  sinalizacao: 'Sinalização',
  energias: 'Fontes de energia',
  funcoesSeguranca: 'Funções de segurança',
  perigos: 'Matriz de perigos',
  emergencias: 'Emergências',
  descricao: 'Descrição funcional',
  aplicacao: 'Aplicação',
  principioFuncionamento: 'Princípio de funcionamento',
  vistas: 'Vistas da máquina',
  especGerais: 'Especificações gerais',
  especEletricas: 'Especificações elétricas',
  especHidraulicas: 'Especificações hidráulicas',
  especPneumaticas: 'Especificações pneumáticas',
  comandos: 'Comandos e indicadores',
  preparacao: 'Sequência de preparação',
  operacao: 'Sequência de operação',
  parada: 'Sequência de parada',
  emergenciaEtapas: 'Atuação em emergência',
  usosImprevisiveis: 'Uso imprevisível',
  planoManutencao: 'Plano de manutenção',
  lubrificacao: 'Lubrificação',
  limpeza: 'Limpeza',
  falhas: 'Lista de falhas',
  glossario: 'Glossário',
  abreviaturas: 'Abreviaturas',
  opcoes: 'Opções de composição',
};

const nomeDoCampo = (campo: string) => NOME_CAMPO[campo] ?? campo;

export function PainelVersoes({ editor, aoFechar }: { editor: ManualEditor; aoFechar: () => void }) {
  const { manualId, manual, marcaVersoes, registrarVersao, voltarParaVersao } = editor;
  const [versoes, setVersoes] = useState<ManualVersaoResumo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [resumo, setResumo] = useState('');
  const [subirRevisao, setSubirRevisao] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [comparando, setComparando] = useState<number | null>(null);
  const [diferencas, setDiferencas] = useState<DiferencaVersao[] | null>(null);

  const recarregar = useCallback(async () => {
    if (!manualId) return;
    setCarregando(true);
    try {
      setVersoes(await listarVersoes(manualId));
      setErro(null);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível carregar o histórico.');
    } finally {
      setCarregando(false);
    }
  }, [manualId]);

  useEffect(() => { void recarregar(); }, [recarregar, marcaVersoes]);

  const gravarVersao = async () => {
    setGravando(true);
    const ok = await registrarVersao({ resumo: resumo.trim() || undefined, bumpRevisao: subirRevisao });
    setGravando(false);
    if (ok) {
      setResumo('');
      setSubirRevisao(false);
    }
  };

  const restaurar = async (numero: number) => {
    if (!window.confirm(`Restaurar a versão ${numero}? O conteúdo atual é guardado antes, então nada se perde.`)) return;
    await voltarParaVersao(numero);
  };

  /** Compara com a versão imediatamente anterior — a pergunta usual. */
  const comparar = async (versao: ManualVersaoResumo, indice: number) => {
    const anterior = versoes[indice + 1];
    if (!manualId || !anterior) return;
    if (comparando === versao.numero) {
      setComparando(null);
      setDiferencas(null);
      return;
    }
    setComparando(versao.numero);
    setDiferencas(null);
    try {
      const resultado = await compararVersoes(manualId, anterior.numero, versao.numero);
      setDiferencas(resultado.diferencas);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível comparar as versões.');
      setComparando(null);
    }
  };

  return (
    <aside className="manual-versoes">
      <header>
        <div>
          <strong><History />Histórico de versões</strong>
          <small>{manual ? `${manual.codigo} · Rev. ${String(manual.revisao).padStart(2, '0')}` : 'Manual sem registro'}</small>
        </div>
        <button type="button" onClick={aoFechar} title="Fechar"><X /></button>
      </header>

      <div className="manual-versoes-nova">
        <label>
          Resumo desta versão
          <input
            type="text"
            value={resumo}
            maxLength={200}
            placeholder="Ex.: incluídos perigos do sistema hidráulico"
            onChange={(evento) => setResumo(evento.target.value)}
          />
        </label>
        <label className="manual-versoes-check">
          <input type="checkbox" checked={subirRevisao} onChange={(evento) => setSubirRevisao(evento.target.checked)} />
          <span>
            Subir a revisão do documento
            <small>Passa a capa e o cabeçalho para Rev. {String((manual?.revisao ?? 0) + 1).padStart(2, '0')}.</small>
          </span>
        </label>
        <button type="button" className="manual-primary-button is-compact" disabled={gravando} onClick={() => void gravarVersao()}>
          {gravando ? <Loader2 className="gira" /> : <Save />}
          Registrar versão
        </button>
      </div>

      {erro && <p className="manual-versoes-erro">{erro}</p>}

      <div className="manual-versoes-lista">
        {carregando ? (
          <p className="manual-versoes-vazio"><Loader2 className="gira" />Carregando histórico…</p>
        ) : versoes.length === 0 ? (
          <p className="manual-versoes-vazio">
            Nenhuma versão registrada ainda. O texto é salvo automaticamente; registre uma versão
            para guardar um ponto de retorno com data, autor e resumo.
          </p>
        ) : (
          versoes.map((versao, indice) => (
            <article key={versao.id} className={comparando === versao.numero ? 'is-aberta' : ''}>
              <header>
                <strong>Versão {versao.numero}</strong>
                <span>Rev. {String(versao.revisao).padStart(2, '0')}</span>
                <i>{rotuloStatus(versao.status)}</i>
              </header>
              <p className="manual-versoes-motivo">{ROTULO_MOTIVO[versao.motivo] ?? versao.motivo}</p>
              {versao.resumo && <p className="manual-versoes-resumo">{versao.resumo}</p>}
              <footer>
                <small>
                  {formatarMomento(versao.createdAt)}
                  {versao.autorNome ? ` · ${versao.autorNome}` : ''} · {versao.progresso}% · {versao.totalPaginas} pág.
                </small>
                <div>
                  {indice + 1 < versoes.length && (
                    <button type="button" className="manual-ghost-button" onClick={() => void comparar(versao, indice)}>
                      <GitCompare />{comparando === versao.numero ? 'Ocultar' : 'Comparar'}
                    </button>
                  )}
                  <button type="button" className="manual-ghost-button" onClick={() => void restaurar(versao.numero)}>
                    <RotateCcw />Restaurar
                  </button>
                </div>
              </footer>

              {comparando === versao.numero && (
                <div className="manual-versoes-diff">
                  {diferencas === null ? (
                    <p><Loader2 className="gira" />Comparando com a versão {versoes[indice + 1]?.numero}…</p>
                  ) : diferencas.length === 0 ? (
                    <p>Nenhuma diferença em relação à versão {versoes[indice + 1]?.numero}.</p>
                  ) : (
                    <ul>
                      {diferencas.map((diferenca) => (
                        <li key={diferenca.campo}>
                          <strong>{nomeDoCampo(diferenca.campo)}</strong>
                          <span><em>{diferenca.de}</em> → <b>{diferenca.para}</b></span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </article>
          ))
        )}
      </div>
    </aside>
  );
}
